import React, { useCallback, useEffect, useMemo, useState } from "react";
import { T, STATUS } from "../theme/tokens.js";
import { Panel, Btn, Notice, Empty, Kpi } from "./ui/index.jsx";
import { supabase, friendlyError } from "../lib/supabase.js";
import { useFallbackPoll } from "../lib/useLive.js";
import { buildRoster } from "../lib/roster.js";
import {
  isoMonday, addWeeks, weekLabel, weekColumns, indexHours,
  utilizationGrid, utilizationSummary, utilizationRollup,
} from "../lib/utilization.js";
import { today } from "../lib/format.js";

/* The utilization grid: hours a week, read against a real week.
 *
 * This is where committed share becomes a true number. Each person's hours are
 * entered per program per week, and the grid reads them against the hours they
 * actually had, holidays taken out. Over a hundred is kept and flagged, because
 * that is the overbooking leadership needs to see, not hide. Exception first:
 * anyone who crosses a full load in the window sits at the top.
 */

const WINDOW = 6;   // weeks shown at once

/* A faint wash behind a cell so the grid reads at a glance: red over a full
   load, amber getting full, green healthy, nothing when there are no hours. */
function tint(pct) {
  if (pct === null || pct === undefined) return "transparent";
  if (pct > 100) return "rgba(143,43,37,0.12)";
  if (pct >= 85) return "rgba(192,118,26,0.12)";
  if (pct >= 50) return "rgba(62,155,98,0.10)";
  return "transparent";
}
function pctInk(pct) {
  if (pct === null || pct === undefined) return T.soft;
  if (pct > 100) return STATUS.overdue.ink;
  if (pct >= 85) return STATUS.inProgress.ink;
  return T.ink;
}

export default function WeeklyHours({ contracts = [], isAdmin }) {
  const [workers, setWorkers] = useState(null);
  const [byContract, setByContract] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [hoursRows, setHoursRows] = useState([]);
  const [error, setError] = useState("");
  /* Open on the last six weeks, not the week ahead. These are logged hours, so
     the weeks worth seeing first are the ones just gone, not an empty future. */
  const [startMonday, setStartMonday] = useState(() => addWeeks(isoMonday(today()), -(WINDOW - 1)));
  const [openId, setOpenId] = useState(null);
  const [rollupBy, setRollupBy] = useState("workday_manager");

  const contractIds = useMemo(() => contracts.map((c) => c.id), [contracts]);
  const weeks = useMemo(() => weekColumns(startMonday, WINDOW), [startMonday]);

  const load = useCallback(async () => {
    const [w, r, h, hrs] = await Promise.all([
      supabase.from("workers").select("*").is("deleted_at", null).order("name"),
      contractIds.length
        ? supabase.from("resources").select("*").in("contract_id", contractIds).is("deleted_at", null)
        : Promise.resolve({ data: [] }),
      supabase.from("company_holidays").select("*"),
      supabase.from("worker_week_hours").select("*")
        .gte("week_start", weeks[0]).lte("week_start", weeks[weeks.length - 1]),
    ]);
    for (const res of [w, r, h, hrs]) if (res.error) { setError(friendlyError(res.error)); return; }
    setWorkers(w.data ?? []);
    const buckets = {};
    for (const row of r.data ?? []) (buckets[row.contract_id] ||= []).push(row);
    setByContract(buckets);
    setHolidays(h.data ?? []);
    setHoursRows(hrs.data ?? []);
    setError("");
  }, [contractIds, weeks]);
  useEffect(() => { load(); }, [load]);
  useFallbackPoll(load, false, 30);

  const assignmentsByWorker = useMemo(() => {
    if (!workers) return new Map();
    const { people } = buildRoster({ workers, byContract, contracts });
    return new Map(people.map((p) => [p.worker.id, p.assignments]));
  }, [workers, byContract, contracts]);

  const grid = useMemo(
    () => (workers ? utilizationGrid({ workers, hoursRows, weeks, holidays }) : { rows: [], weeks, byCell: new Map() }),
    [workers, hoursRows, weeks, holidays]);
  const summary = useMemo(() => utilizationSummary(grid), [grid]);
  const rollup = useMemo(() => utilizationRollup(grid, (wk) =>
    rollupBy === "home_location" ? wk.home_location
      : rollupBy === "hire_type" ? wk.hire_type : wk.workday_manager), [grid, rollupBy]);

  const contractName = useMemo(
    () => new Map(contracts.map((c) => [c.id, c.name])), [contracts]);

  /* Set one cell: a worker's hours on one contract in one week. Zero or blank
     clears the row rather than storing a zero, so the table stays sparse. */
  const setHours = async (worker_id, contract_id, week_start, raw) => {
    const hours = raw === "" || raw === null ? null : Math.max(0, Number(raw));
    setError("");
    if (hours === null || hours === 0) {
      const { error: e } = await supabase.from("worker_week_hours").delete()
        .match({ worker_id, contract_id, week_start });
      if (e) { setError(friendlyError(e)); return; }
    } else {
      const { error: e } = await supabase.from("worker_week_hours")
        .upsert({ worker_id, contract_id, week_start, hours }, { onConflict: "worker_id,contract_id,week_start" });
      if (e) { setError(friendlyError(e)); return; }
    }
    await load();
  };

  if (workers === null && !error) return <Panel title="Utilization"><Empty text="Loading hours..." /></Panel>;

  const rangeLabel = `${weekLabel(weeks[0])} to ${weekLabel(addWeeks(weeks[weeks.length - 1], 0))}`;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {error && <Notice kind="error">{error}</Notice>}

      <Panel
        title="Utilization, week by week"
        right={
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Btn small kind="ghost" onClick={() => setStartMonday(addWeeks(startMonday, -WINDOW))}>‹ Earlier</Btn>
            <Btn small kind="ghost" onClick={() => setStartMonday(addWeeks(isoMonday(today()), -(WINDOW - 1)))}>Recent</Btn>
            <Btn small kind="ghost" onClick={() => setStartMonday(addWeeks(startMonday, WINDOW))}>Later ›</Btn>
          </span>
        }
      >
        <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 14, lineHeight: 1.6 }}>
          Hours each week against the hours each person actually had, with company holidays and
          their own floating day taken out, so a short week reads at a hundred percent rather than
          looking slack. Over a full load is kept and flagged, not hidden.
          {isAdmin && <> Open a person to enter or change their hours by program.</>}
          {" "}Showing {rangeLabel}.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 10, marginBottom: 16 }}>
          <Kpi label="People with hours" value={summary.people} sub={`in ${WINDOW} weeks`} />
          <Kpi label="Equivalent heads" value={summary.fte} sub="full-time equivalent" />
          <Kpi label="Over a full load" value={summary.overThisWindow}
            sub="in any week shown" color={summary.overThisWindow ? STATUS.overdue.ink : undefined} />
          <Kpi label="Hours booked" value={Math.round(summary.periodHours)} sub="across the window" />
        </div>

        {grid.rows.length === 0
          ? <Empty text="No one on the roster yet. Add people on the Roster tab first." />
          : (
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 640, border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
                <Header weeks={weeks} />
                {grid.rows.map((row) => (
                  <WorkerRow
                    key={row.worker.id}
                    row={row}
                    weeks={weeks}
                    open={openId === row.worker.id}
                    onToggle={() => setOpenId(openId === row.worker.id ? null : row.worker.id)}
                    assignments={assignmentsByWorker.get(row.worker.id) || []}
                    byCell={grid.byCell}
                    contractName={contractName}
                    isAdmin={isAdmin}
                    onSet={setHours}
                  />
                ))}
              </div>
            </div>
          )}
      </Panel>

      {grid.rows.length > 0 && (
        <Panel
          title="By the slice leadership reads"
          right={
            <span style={{ display: "flex", gap: 6 }}>
              {[["workday_manager", "Manager"], ["home_location", "Location"], ["hire_type", "Hire type"]].map(([k, label]) => (
                <Btn key={k} small kind={rollupBy === k ? "gold" : "ghost"} onClick={() => setRollupBy(k)}>{label}</Btn>
              ))}
            </span>
          }
        >
          {rollup.length === 0
            ? <Empty text="No hours booked in this window yet." />
            : (
              <div style={{ display: "grid", gap: 1, background: T.line,
                border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1.4fr 0.7fr 0.8fr 0.8fr",
                  gap: 10, padding: "7px 12px", background: T.bg, fontSize: 10.5, fontWeight: 700,
                  letterSpacing: "0.06em", textTransform: "uppercase", color: T.soft }}>
                  <span>Group</span><span>People</span><span>FTE</span><span>Over load</span>
                </div>
                {rollup.map((g) => (
                  <div key={g.key} style={{ display: "grid", gridTemplateColumns: "1.4fr 0.7fr 0.8fr 0.8fr",
                    gap: 10, alignItems: "center", background: T.panel, padding: "8px 12px", fontSize: 13 }}>
                    <span style={{ fontWeight: 600, color: T.navy }}>{g.key}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{g.headcount}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{g.fte}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums",
                      color: g.over ? STATUS.overdue.ink : T.soft, fontWeight: g.over ? 700 : 400 }}>{g.over}</span>
                  </div>
                ))}
              </div>
            )}
        </Panel>
      )}
    </div>
  );
}

function Header({ weeks }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `1.6fr repeat(${weeks.length}, 1fr) 0.7fr`,
      gap: 0, background: T.bg, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em",
      textTransform: "uppercase", color: T.soft, borderBottom: `1px solid ${T.line}` }}>
      <span style={{ padding: "8px 12px" }}>Person</span>
      {weeks.map((wk) => (
        <span key={wk} style={{ padding: "8px 6px", textAlign: "center" }}>{weekLabel(wk)}</span>
      ))}
      <span style={{ padding: "8px 8px", textAlign: "right" }}>FTE</span>
    </div>
  );
}

function WorkerRow({ row, weeks, open, onToggle, assignments, byCell, contractName, isAdmin, onSet }) {
  const cols = `1.6fr repeat(${weeks.length}, 1fr) 0.7fr`;
  return (
    <div style={{ background: T.panel, borderBottom: `1px solid ${T.line}` }}>
      <div onClick={onToggle} style={{ display: "grid", gridTemplateColumns: cols, gap: 0,
        alignItems: "center", cursor: "pointer",
        borderLeft: row.weeksOver > 0 ? `3px solid ${STATUS.overdue.fill}` : "3px solid transparent" }}>
        <span style={{ padding: "9px 12px" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{row.name}</span>
          <span style={{ marginLeft: 7, fontSize: 10.5, color: T.soft }}>{open ? "▾" : "▸"}</span>
        </span>
        {row.cells.map((c) => (
          <span key={c.weekStart} title={c.pct === null ? "no capacity" : `${c.pct}% of ${c.available}h`}
            style={{ padding: "9px 6px", textAlign: "center", fontSize: 12.5,
              fontVariantNumeric: "tabular-nums", background: tint(c.pct), color: pctInk(c.pct),
              fontWeight: c.pct !== null && c.pct > 100 ? 700 : 500 }}>
            {c.hours ? c.hours : <span style={{ color: T.soft }}>·</span>}
            {c.pct !== null && c.hours > 0 && (
              <div style={{ fontSize: 9.5, color: pctInk(c.pct) }}>{c.pct}%</div>
            )}
          </span>
        ))}
        <span style={{ padding: "9px 8px", textAlign: "right", fontSize: 12.5, fontWeight: 700,
          color: T.navy, fontVariantNumeric: "tabular-nums" }}>{row.fte ?? "—"}</span>
      </div>

      {open && (
        <div style={{ padding: "4px 12px 12px", background: T.bg }}>
          {assignments.length === 0
            ? <div style={{ fontSize: 12, color: T.soft, padding: "8px 0" }}>
                Not on any contract's resource list yet. Add them to a contract on the Roster tab,
                then their hours can be entered here.
              </div>
            : assignments.map((a) => (
                <div key={a.contractId} style={{ display: "grid", gridTemplateColumns: cols, gap: 0,
                  alignItems: "center", padding: "3px 0", borderBottom: `1px solid ${T.line}` }}>
                  <span style={{ padding: "4px 12px", fontSize: 12, color: T.ink }}>{a.contractName}</span>
                  {weeks.map((wk) => {
                    const v = byCell.get(`${row.worker.id}|${a.contractId}|${wk}`) || "";
                    return (
                      <span key={wk} style={{ padding: "2px 4px", textAlign: "center" }}>
                        {isAdmin
                          ? <HoursInput value={v}
                              onCommit={(val) => onSet(row.worker.id, a.contractId, wk, val)} />
                          : <span style={{ fontSize: 12, color: T.ink }}>{v || "·"}</span>}
                      </span>
                    );
                  })}
                  <span />
                </div>
              ))}
        </div>
      )}
    </div>
  );
}

/* A small hours cell that only writes when it loses focus or takes Enter, so a
   half-typed number is never saved and the grid is not hammered on every key. */
function HoursInput({ value, onCommit }) {
  const [v, setV] = useState(value === "" ? "" : String(value));
  useEffect(() => { setV(value === "" ? "" : String(value)); }, [value]);
  const commit = () => { if (String(value) !== v) onCommit(v); };
  return (
    <input
      value={v}
      inputMode="decimal"
      onChange={(e) => setV(e.target.value.replace(/[^0-9.]/g, ""))}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      style={{ width: "100%", maxWidth: 52, textAlign: "center", fontSize: 12,
        border: `1px solid ${T.line}`, borderRadius: 4, padding: "4px 2px",
        background: T.panel, color: T.ink, fontVariantNumeric: "tabular-nums" }}
    />
  );
}
